import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { type SxProps, styled, type Theme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

import { type Props as ImageProps, Image as UiImage } from '../Image/Image';

type Props = ImageProps & {
  placeholderSrc?: string;
  src: string;
  showLoader?: boolean;
  sx?: SxProps<Theme>;
  zoomOnHover?: boolean;
  minHeight?: string | number;
};

type ImageLoadState = {
  loaded: boolean;
  src: string;
};

const Container = styled(Box, {
  shouldForwardProp: (prop) => !['rounded', 'minHeight'].includes(prop as string),
})<Pick<Props, 'rounded' | 'minHeight'>>(({ rounded, theme, minHeight }) => {
  const numericRadius =
    typeof theme.shape.borderRadius === 'number'
      ? theme.shape.borderRadius
      : Number.parseFloat(theme.shape.borderRadius);
  const baseRadius = Number.isFinite(numericRadius) ? numericRadius : 0;

  return {
    borderRadius: rounded ? baseRadius * 2 : 0,
    display: 'flex',
    height: 'inherit',
    minHeight,
    overflow: 'hidden',
    width: 'inherit',
  };
});

const LoaderContainer = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
});

export const ProgressiveImage = ({
  placeholderSrc,
  src,
  sx = {},
  showLoader = true,
  zoomOnHover = false,
  minHeight = 'auto',
  ...props
}: Props) => {
  const [loadState, setLoadState] = useState<ImageLoadState>({ loaded: false, src });
  const isLoading = loadState.src !== src || !loadState.loaded;
  const imgSrc = isLoading ? placeholderSrc || src : src;
  const imageSx = Array.isArray(sx) ? sx : [sx];

  useEffect(() => {
    let isCancelled = false;
    const img = new Image();

    const handleLoad = () => {
      if (!isCancelled) {
        setLoadState({ loaded: true, src });
      }
    };

    img.addEventListener('load', handleLoad);
    img.src = src;

    if (img.complete) {
      queueMicrotask(handleLoad);
    }

    return () => {
      isCancelled = true;
      img.removeEventListener('load', handleLoad);
    };
  }, [src]);

  return (
    <Container
      rounded={props.rounded}
      minHeight={minHeight}
      sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', position: 'relative' }}
    >
      {showLoader && isLoading ? (
        <LoaderContainer>
          <Skeleton variant="rectangular" width="100%" height="100%" />
        </LoaderContainer>
      ) : null}
      <UiImage
        {...props}
        src={imgSrc}
        sx={[
          {
            '&:hover': {
              transform: zoomOnHover ? 'scale(1.2)' : 'none',
            },
          },
          placeholderSrc && imgSrc === placeholderSrc
            ? {
                clipPath: 'inset(0)',
                filter: 'blur(10px)',
                opacity: 0.5,
              }
            : {
                filter: 'none',
                opacity: isLoading ? 0 : 1,
                transition: (theme: Theme) =>
                  theme.transitions.create(['filter', 'opacity', 'transform'], {
                    duration: theme.transitions.duration.complex,
                    easing: theme.transitions.easing.easeInOut,
                  }),
              },
          ...imageSx,
        ]}
      />
    </Container>
  );
};
